import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import React from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Card } from '@/components/ui/Card';
import { Screen } from '@/components/ui/Screen';
import { useThemePalette } from '@/config/theme';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import {
  DEFAULT_WIDGET_PREFS,
  WIDGET_ACCENTS,
  WIDGET_STYLES,
  normalizeWidgetPrefs,
  type WidgetAccent,
  type WidgetLayout,
  type WidgetPrefs,
  type WidgetStyle,
} from '@/widgets/MatchWidget';
import { getWidgetPrefs, setWidgetPrefs } from '@/widgets/widgetStyle';

type IoniconName = keyof typeof Ionicons.glyphMap;
const PREVIEW_W = 300;

// Static, always-renders previews of each widget. (The native WidgetPreview was
// unreliable on-device - "Error rendering widget"; the REAL widget still renders
// on the home screen. These mocks just show what it looks like.)
function GlassCard({ accent, children }: { accent: string; children: React.ReactNode }) {
  return (
    <View
      className="overflow-hidden rounded-3xl border"
      style={{ borderColor: `${accent}44`, width: PREVIEW_W }}
    >
      <LinearGradient
        colors={['rgba(31,31,64,0.82)', 'rgba(15,15,34,0.66)']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={{ padding: 16 }}
      >
        {children}
      </LinearGradient>
    </View>
  );
}

const FIXTURES = [
  { home: 'MAR', hf: '🇲🇦', away: 'POR', af: '🇵🇹', t: '9:00 PM' },
  { home: 'EGY', hf: '🇪🇬', away: 'CPV', af: '🇨🇻', t: '11:00 PM' },
  { home: 'KSA', hf: '🇸🇦', away: 'SEN', af: '🇸🇳', t: '2:00 AM' },
];

function MatchMock() {
  return (
    <GlassCard accent="#22D3FF">
      <View className="flex-row items-center justify-between">
        <AppText variant="micro" weight="bold" style={{ color: '#9698BE' }}>World Cup 2026</AppText>
        <View className="flex-row items-center gap-1">
          <View style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: '#22D3FF' }} />
          <AppText variant="micro" weight="bold" style={{ color: '#5FE9FF', letterSpacing: 1 }}>LIVE</AppText>
        </View>
      </View>
      <View className="mt-3 gap-2.5">
        {FIXTURES.map((m, i) => (
          <View
            key={m.home}
            className="flex-row items-center justify-between"
            style={{ paddingTop: i === 0 ? 0 : 9, borderTopWidth: i === 0 ? 0 : 1, borderTopColor: 'rgba(255,255,255,0.06)' }}
          >
            <View className="flex-1 flex-row items-center gap-1.5">
              <AppText style={{ fontSize: 15 }}>{m.hf}</AppText>
              <AppText variant="caption" weight="bold" style={{ color: '#F1F2F8' }}>{m.home}</AppText>
            </View>
            <AppText variant="caption" weight="bold" tabular style={{ color: '#F1F2F8' }}>{m.t}</AppText>
            <View className="flex-1 flex-row items-center justify-end gap-1.5">
              <AppText variant="caption" weight="bold" style={{ color: '#F1F2F8' }}>{m.away}</AppText>
              <AppText style={{ fontSize: 15 }}>{m.af}</AppText>
            </View>
          </View>
        ))}
      </View>
    </GlassCard>
  );
}


// ---------------------------------------------------------------------------
// Widget STYLE picker. Four selectable looks (mirrors MatchWidget's themes):
// dark / gradient / glass / minimal. The chosen value is persisted to
// AsyncStorage (tashkeela.widgetStyle) and the live home-screen tile is asked
// to redraw via requestWidgetUpdate (best-effort, Android only).
// ---------------------------------------------------------------------------

/** Visual recipe for each style's in-app preview swatch (NOT the live widget — */
/** that renders natively. These mirror the look so the choice is obvious).     */
type Swatch = {
  plate: [string, string]; // plate gradient (top -> bottom)
  panel: [string, string]; // inset card gradient
  border: string;
  accent: string;
  ink: string;
  soft: string;
  chip: string;
  chipLine: string;
};

const SWATCHES: Record<WidgetStyle, Swatch> = {
  gradient: {
    plate: ['#1C1D3A', '#0E0F22'],
    panel: ['#26284C', '#191A35'],
    border: '#22D3FF52',
    accent: '#5FE9FF',
    ink: '#F4F5FB',
    soft: '#C9CCEA',
    chip: 'rgba(255,255,255,0.07)',
    chipLine: 'rgba(255,255,255,0.14)',
  },
  dark: {
    plate: ['#16172C', '#16172C'],
    panel: ['#1E1F3A', '#1E1F3A'],
    border: '#22D3FF3D',
    accent: '#5FE9FF',
    ink: '#F4F5FB',
    soft: '#C9CCEA',
    chip: 'rgba(255,255,255,0.06)',
    chipLine: 'rgba(255,255,255,0.12)',
  },
  glass: {
    plate: ['rgba(28,29,58,0.55)', 'rgba(14,15,34,0.40)'],
    panel: ['rgba(42,44,82,0.50)', 'rgba(25,26,53,0.45)'],
    border: '#22D3FF66',
    accent: '#7DEEFF',
    ink: '#F8F9FE',
    soft: '#D6D9F2',
    chip: 'rgba(255,255,255,0.12)',
    chipLine: 'rgba(255,255,255,0.20)',
  },
  minimal: {
    plate: ['#222338', '#1B1C2E'],
    panel: ['#2A2B42', '#26273C'],
    border: 'rgba(255,255,255,0.12)',
    accent: '#C6CBE6',
    ink: '#F2F3F9',
    soft: '#BFC2DA',
    chip: 'rgba(255,255,255,0.05)',
    chipLine: 'rgba(255,255,255,0.10)',
  },
};

/**
 * In-app accent swatches — mirror MatchWidget's ACCENTS so the picker preview
 * matches the live tile. `accentHex` from here is threaded into the style mocks
 * and the live redraw. `cyan` is the default (reproduces the shipped look).
 */
const ACCENT_HEX: Record<WidgetAccent, string> = {
  cyan: '#5FE9FF',
  violet: '#B9AEFF',
  emerald: '#6FF3C4',
  amber: '#FFD27A',
  rose: '#FF9EC2',
};

/**
 * A compact match-card mock painted in the given style — the "glass plate +
 * inset score chip" silhouette of the real widget. `glass` deliberately shows a
 * faux wallpaper behind it so the see-through effect reads at a glance. The
 * `accent` override recolors the brand strip so the swatch reflects the chosen
 * accent color (falls back to the style's own accent when omitted).
 */
function StyleMiniPreview({ style, accent }: { style: WidgetStyle; accent?: string }) {
  const base = SWATCHES[style];
  const s = accent ? { ...base, accent } : base;
  const isGlass = style === 'glass';
  const card = (
    <LinearGradient
      colors={s.plate}
      start={{ x: 0, y: 0 }}
      end={{ x: 0, y: 1 }}
      style={{ borderRadius: 16, borderWidth: 1, borderColor: s.border, padding: 11 }}
    >
      {/* Header: accent strip + competition + kickoff chip */}
      <View className="flex-row items-center justify-between">
        <View className="flex-row items-center" style={{ gap: 6 }}>
          <View style={{ width: 3, height: 12, borderRadius: 2, backgroundColor: s.accent }} />
          <View style={{ width: 46, height: 5, borderRadius: 3, backgroundColor: s.soft, opacity: 0.55 }} />
        </View>
        <View style={{ width: 26, height: 9, borderRadius: 4, backgroundColor: s.chip, borderWidth: 1, borderColor: s.chipLine }} />
      </View>
      {/* Inset card: two crests + score chip */}
      <LinearGradient
        colors={s.panel}
        start={{ x: 0, y: 0 }}
        end={{ x: 0, y: 1 }}
        style={{
          marginTop: 9,
          borderRadius: 12,
          borderWidth: 1,
          borderColor: 'rgba(255,255,255,0.10)',
          paddingVertical: 10,
          paddingHorizontal: 8,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
        }}
      >
        <View style={{ width: 22, height: 16, borderRadius: 4, backgroundColor: s.chip, borderWidth: 1, borderColor: s.chipLine }} />
        <View
          style={{
            paddingHorizontal: 9,
            paddingVertical: 5,
            borderRadius: 9,
            backgroundColor: s.chip,
            borderWidth: 1,
            borderColor: s.chipLine,
          }}
        >
          <AppText weight="bold" tabular style={{ fontSize: 14, color: s.ink }}>2 - 1</AppText>
        </View>
        <View style={{ width: 22, height: 16, borderRadius: 4, backgroundColor: s.chip, borderWidth: 1, borderColor: s.chipLine }} />
      </LinearGradient>
    </LinearGradient>
  );

  if (!isGlass) return card;
  // Glass: layer the card over a faux wallpaper so the translucency is visible.
  return (
    <View className="overflow-hidden rounded-2xl">
      <LinearGradient
        colors={['#3B2F6B', '#7C5BD6', '#2BC6C0']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={{ padding: 7 }}
      >
        {card}
      </LinearGradient>
    </View>
  );
}

/** One selectable style tile: preview swatch + label + selected check. */
function StyleTile({
  style,
  accent,
  selected,
  onPress,
}: {
  style: WidgetStyle;
  accent: string;
  selected: boolean;
  onPress: () => void;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="radio"
      accessibilityState={{ selected }}
      style={{ width: '48%' }}
    >
      <View
        className="overflow-hidden rounded-2xl p-2.5"
        style={{
          borderColor: selected ? palette.voltBright : palette.glassBorder,
          borderWidth: selected ? 2 : 1,
          backgroundColor: selected ? palette.voltGlass : 'transparent',
        }}
      >
        <StyleMiniPreview style={style} accent={accent} />
        <View className="mt-2.5 flex-row items-center justify-between">
          <AppText variant="caption" weight="bold" className="text-ink">
            {t(`widgetStyles.${style}Name`)}
          </AppText>
          <Ionicons
            name={selected ? 'checkmark-circle' : 'ellipse-outline'}
            size={18}
            color={selected ? palette.voltBright : palette.muted}
          />
        </View>
        <AppText variant="micro" className="mt-0.5 text-muted" numberOfLines={2}>
          {t(`widgetStyles.${style}Desc`)}
        </AppText>
      </View>
    </Pressable>
  );
}

/** A single tappable accent color swatch (selected = ring + check dot). */
function AccentSwatch({
  hex,
  selected,
  onPress,
  label,
}: {
  hex: string;
  selected: boolean;
  onPress: () => void;
  label: string;
}) {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="radio"
      accessibilityState={{ selected }}
      accessibilityLabel={label}
      className="items-center justify-center"
      style={{
        width: 40,
        height: 40,
        borderRadius: 20,
        borderWidth: selected ? 2 : 1,
        borderColor: selected ? hex : 'rgba(255,255,255,0.16)',
      }}
    >
      <View style={{ width: 24, height: 24, borderRadius: 12, backgroundColor: hex }} />
    </Pressable>
  );
}

/** A two-option segmented control (layout chooser). */
function LayoutSegment({
  options,
  value,
  onChange,
}: {
  options: { key: WidgetLayout; label: string; icon: IoniconName }[];
  value: WidgetLayout;
  onChange: (v: WidgetLayout) => void;
}) {
  const palette = useThemePalette();
  return (
    <View
      className="flex-row overflow-hidden rounded-xl border"
      style={{ borderColor: palette.glassBorder }}
    >
      {options.map((opt) => {
        const active = opt.key === value;
        return (
          <Pressable
            key={opt.key}
            onPress={() => onChange(opt.key)}
            accessibilityRole="radio"
            accessibilityState={{ selected: active }}
            className="flex-1 flex-row items-center justify-center gap-1.5 py-2.5"
            style={{ backgroundColor: active ? palette.voltGlass : 'transparent' }}
          >
            <Ionicons name={opt.icon} size={15} color={active ? palette.voltBright : palette.muted} />
            <AppText
              variant="caption"
              weight={active ? 'bold' : 'medium'}
              style={{ color: active ? palette.voltBright : palette.soft }}
            >
              {opt.label}
            </AppText>
          </Pressable>
        );
      })}
    </View>
  );
}

function WidgetStylePicker() {
  const { t } = useTranslation();
  const { isRTL } = useLocale();
  const palette = useThemePalette();
  const [prefs, setPrefs] = React.useState<WidgetPrefs>(DEFAULT_WIDGET_PREFS);

  React.useEffect(() => {
    let alive = true;
    void getWidgetPrefs().then((p) => {
      if (alive) setPrefs(p);
    });
    return () => {
      alive = false;
    };
  }, []);

  // Persist + best-effort live redraw whenever any field changes. Android only;
  // never throws into the UI (native module absent on web/iOS, or no widget yet).
  const apply = React.useCallback((patch: Partial<WidgetPrefs>) => {
    setPrefs((prev) => {
      const next = normalizeWidgetPrefs({ ...prev, ...patch });
      void setWidgetPrefs(next);
      void (async () => {
        try {
          const { requestWidgetUpdate } = await import('react-native-android-widget');
          const { MatchWidget } = await import('@/widgets/MatchWidget');
          const { fetchWidgetData } = await import('@/widgets/matchData');
          const data = await fetchWidgetData().catch(() => ({
            focus: null,
            fixtures: [],
            standings: [],
            followedTeamIds: [],
          }));
          await requestWidgetUpdate({
            widgetName: 'Match',
            renderWidget: (info) => (
              <MatchWidget
                data={data}
                width={info.width > 0 ? info.width : 180}
                height={info.height > 0 ? info.height : 110}
                style={next.style}
                accent={next.accent}
                layout={next.layout}
                favoriteFirst={next.favoriteFirst}
              />
            ),
            widgetNotFound: () => {
              // No widget on the home screen yet — nothing to redraw. Fine.
            },
          });
        } catch {
          // Native module unavailable (web/iOS) or no widget added — preference
          // is saved regardless and applies on the next render.
        }
      })();
      return next;
    });
  }, []);

  const accentHex = ACCENT_HEX[prefs.accent];

  return (
    <View style={{ gap: 18 }}>
      {/* STYLE */}
      <View>
        <AppText variant="caption" weight="bold" className="mx-4 mt-1 uppercase tracking-wide text-soft">
          {t('widgetStyles.title')}
        </AppText>
        <AppText variant="micro" className="mx-4 mb-1 mt-1 text-muted">
          {t('widgetStyles.subtitle')}
        </AppText>
        <View
          className="mx-4 flex-wrap justify-between"
          style={{ gap: 12, flexDirection: isRTL ? 'row-reverse' : 'row' }}
        >
          {WIDGET_STYLES.map((style) => (
            <StyleTile
              key={style}
              style={style}
              accent={accentHex}
              selected={prefs.style === style}
              onPress={() => apply({ style })}
            />
          ))}
        </View>
      </View>

      {/* ACCENT COLOR */}
      <View>
        <AppText variant="caption" weight="bold" className="mx-4 uppercase tracking-wide text-soft">
          {t('widgetStyles.accentTitle')}
        </AppText>
        <AppText variant="micro" className="mx-4 mb-2 mt-1 text-muted">
          {t('widgetStyles.accentSubtitle')}
        </AppText>
        <View
          className="mx-4 flex-row items-center"
          style={{ gap: 14, flexDirection: isRTL ? 'row-reverse' : 'row' }}
        >
          {WIDGET_ACCENTS.map((accent) => (
            <AccentSwatch
              key={accent}
              hex={ACCENT_HEX[accent]}
              selected={prefs.accent === accent}
              onPress={() => apply({ accent })}
              label={t(`widgetStyles.accent_${accent}`)}
            />
          ))}
        </View>
      </View>

      {/* LAYOUT */}
      <View>
        <AppText variant="caption" weight="bold" className="mx-4 uppercase tracking-wide text-soft">
          {t('widgetStyles.layoutTitle')}
        </AppText>
        <AppText variant="micro" className="mx-4 mb-2 mt-1 text-muted">
          {t('widgetStyles.layoutSubtitle')}
        </AppText>
        <View className="mx-4">
          <LayoutSegment
            value={prefs.layout}
            onChange={(layout) => apply({ layout })}
            options={[
              { key: 'auto', label: t('widgetStyles.layoutAuto'), icon: 'football-outline' },
              { key: 'upcoming', label: t('widgetStyles.layoutUpcoming'), icon: 'list-outline' },
            ]}
          />
        </View>
      </View>

      {/* FAVORITE FIRST */}
      <Pressable
        onPress={() => apply({ favoriteFirst: !prefs.favoriteFirst })}
        accessibilityRole="switch"
        accessibilityState={{ checked: prefs.favoriteFirst }}
        className="mx-4 flex-row items-center gap-3 rounded-2xl border px-4 py-3"
        style={{
          borderColor: prefs.favoriteFirst ? palette.voltBright : palette.glassBorder,
          backgroundColor: prefs.favoriteFirst ? palette.voltGlass : 'transparent',
        }}
      >
        <Ionicons
          name={prefs.favoriteFirst ? 'star' : 'star-outline'}
          size={20}
          color={prefs.favoriteFirst ? palette.voltBright : palette.muted}
        />
        <View className="flex-1">
          <AppText variant="caption" weight="bold" className="text-ink">
            {t('widgetStyles.favoriteFirstTitle')}
          </AppText>
          <AppText variant="micro" className="text-muted" numberOfLines={2}>
            {t('widgetStyles.favoriteFirstDesc')}
          </AppText>
        </View>
        <Ionicons
          name={prefs.favoriteFirst ? 'checkmark-circle' : 'ellipse-outline'}
          size={22}
          color={prefs.favoriteFirst ? palette.voltBright : palette.muted}
        />
      </Pressable>
    </View>
  );
}

function WidgetCard({
  icon,
  title,
  desc,
  children,
}: {
  icon: IoniconName;
  title: string;
  desc: string;
  children: React.ReactNode;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  return (
    <Card className="mx-4 gap-3 px-4 py-4">
      <View className="flex-row items-center gap-2.5">
        <View className="h-9 w-9 items-center justify-center rounded-xl bg-volt-glass">
          <Ionicons name={icon} size={18} color={palette.voltBright} />
        </View>
        <View className="flex-1">
          <AppText variant="caption" weight="bold" className="text-ink">{title}</AppText>
          <AppText variant="micro" className="text-soft" numberOfLines={2}>{desc}</AppText>
        </View>
        <View className="rounded-full bg-volt/15 px-2.5 py-1">
          <AppText variant="micro" weight="bold" className="uppercase tracking-wide text-volt-bright">
            {t('widgets.free')}
          </AppText>
        </View>
      </View>
      <View className="items-center py-1">{children}</View>
    </Card>
  );
}

function Step({ index, icon, text }: { index: number; icon: IoniconName; text: string }) {
  const palette = useThemePalette();
  return (
    <View className="flex-row items-center gap-3 px-4 py-3">
      <View className="h-8 w-8 items-center justify-center rounded-full bg-volt-glass">
        <AppText variant="caption" weight="bold" tabular className="text-volt-bright">{index}</AppText>
      </View>
      <Ionicons name={icon} size={18} color={palette.muted} />
      <AppText variant="caption" weight="medium" className="flex-1 text-soft">{text}</AppText>
    </View>
  );
}

export default function WidgetsScreen() {
  const palette = useThemePalette();
  const { t } = useTranslation();
  return (
    <Screen>
      <BackHeader title={t('widgets.title')} />
      <ScrollView contentContainerStyle={{ paddingBottom: 36, gap: 14 }}>
        <AppText variant="caption" className="mx-4 mt-1 text-center text-soft">
          {t('widgets.intro')}
        </AppText>

        <WidgetCard icon="football" title={t('widgets.matchName')} desc={t('widgets.matchDesc')}>
          <MatchMock />
        </WidgetCard>

        {/* Widget style picker */}
        <WidgetStylePicker />

        {/* Home screen */}
        <AppText variant="caption" weight="bold" className="mx-4 mt-1 uppercase tracking-wide text-soft">
          {t('widgets.homeTitle')}
        </AppText>
        <Card className="mx-4 overflow-hidden">
          <Step index={1} icon="finger-print-outline" text={t('widgets.homeStep1')} />
          <View className="border-t border-line" />
          <Step index={2} icon="apps-outline" text={t('widgets.homeStep2')} />
          <View className="border-t border-line" />
          <Step index={3} icon="move-outline" text={t('widgets.homeStep3')} />
        </Card>

        {/* Lock screen */}
        <AppText variant="caption" weight="bold" className="mx-4 mt-1 uppercase tracking-wide text-soft">
          {t('widgets.lockTitle')}
        </AppText>
        <Card className="mx-4 overflow-hidden">
          <Step index={1} icon="lock-closed-outline" text={t('widgets.lockStep1')} />
          <View className="border-t border-line" />
          <Step index={2} icon="add-circle-outline" text={t('widgets.lockStep2')} />
        </Card>

        <View className="mx-4 flex-row items-start gap-2 rounded-card border border-line bg-surface px-4 py-3">
          <Ionicons name="information-circle-outline" size={16} color={palette.voltBright} />
          <AppText variant="micro" className="flex-1 leading-5 text-muted">
            {t('widgets.lockNote')}
          </AppText>
        </View>
      </ScrollView>
    </Screen>
  );
}
